// Shared dialog-presence store for animation pause and overlay ordering.

import { useSyncExternalStore } from 'react';

type Listener = () => void;

export type DialogPresenceId = number;

type RegisterOpenDialogOptions = {
  pauseBackground?: boolean;
};

let nextDialogPresenceId = 1;
const openDialogIds = new Set<DialogPresenceId>();
const pauseDialogIds = new Set<DialogPresenceId>();
const listeners = new Set<Listener>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getTopmostDialogId(): DialogPresenceId | null {
  let topmostId: DialogPresenceId | null = null;
  openDialogIds.forEach((id) => {
    if (topmostId === null || id > topmostId) {
      topmostId = id;
    }
  });
  return topmostId;
}

function syncBodyDialogOpenAttribute() {
  if (typeof document === 'undefined') {
    return;
  }

  if (pauseDialogIds.size > 0) {
    document.body.dataset.dialogOpen = 'true';
  } else {
    delete document.body.dataset.dialogOpen;
  }
}

function getAnyDialogOpenSnapshot() {
  return pauseDialogIds.size > 0;
}

function getTopmostDialogSnapshot() {
  return getTopmostDialogId();
}

export function createDialogPresenceId(): DialogPresenceId {
  return nextDialogPresenceId++;
}

export function registerOpenDialog(
  id: DialogPresenceId = createDialogPresenceId(),
  { pauseBackground = true }: RegisterOpenDialogOptions = {}
): () => void {
  let isRegistered = true;

  openDialogIds.add(id);
  if (pauseBackground) {
    pauseDialogIds.add(id);
  }

  syncBodyDialogOpenAttribute();
  emitChange();

  return () => {
    if (!isRegistered) {
      return;
    }
    isRegistered = false;

    openDialogIds.delete(id);
    pauseDialogIds.delete(id);
    syncBodyDialogOpenAttribute();
    emitChange();
  };
}

export function useAnyDialogOpen() {
  return useSyncExternalStore(
    subscribe,
    getAnyDialogOpenSnapshot,
    getAnyDialogOpenSnapshot
  );
}

export function useIsTopmostOverlay(id?: DialogPresenceId) {
  const topmostDialogId = useSyncExternalStore(
    subscribe,
    getTopmostDialogSnapshot,
    getTopmostDialogSnapshot
  );

  return id === undefined || topmostDialogId === null || topmostDialogId === id;
}
